/**
 * Income & spend, second edition: where the month's money came from and went.
 *
 * Four figures; one bar of the income split by where it went; the month's
 * transactions grouped by category, filterable from that bar; and the fixed
 * costs beside them, with a calendar of the days they are charged on. Written
 * once for both layouts (`compact` is the phone).
 */

import { h } from '../vdom.js';
import { localDay, numberText,
  CATEGORIES, CATEGORY_COLORS, CADENCES, currentMonth, nextChargeMonth, recurringFallsIn,
  expenseCategory, parseNumber
} from '../../../shared/model.js';
import {
  C, MONO, css, tap, input, select, emptyLine, expenseLabel, expenseText, incomeLabel,
  planLimit, planLine, saveFile, currencyPicker, currencyOptions, categoryChoices
} from './common.js';
import { CASH, DANGER_TEXT, GAIN_BG, GAIN_LINE, GAIN_LABEL } from './whatif.js';

const pct = ratio => (Math.max(0, ratio) * 100).toFixed(2) + '%';
/** A category's colour; one of one's own gets the neutral grey of the design. */
export const categoryColor = key => CATEGORY_COLORS[key] || CASH;

/* Dark text on a light swatch, light text on a dark one, for figures written inside a bar. */
const inkOn = hex => {
  const value = parseInt(String(hex).replace('#', '').slice(0, 6), 16);
  if (!Number.isFinite(value)) return C.text;
  const r = (value >> 16) & 255, g = (value >> 8) & 255, b = value & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? '#04120C' : '#F2EFE9';
};

const box = (compact, style, ...children) => h('div', {
  style: css({
    border: `1px solid ${C.stroke}`, borderRadius: compact ? '16px' : '14px', background: C.card,
    display: 'flex', flexDirection: 'column', minWidth: 0, ...style
  })
}, ...children);

const title = (compact, text) => h('div', { style: css({ fontSize: compact ? '14px' : '15px', fontWeight: 700 }) }, text);
const hint = text => h('div', { style: css({ fontSize: '12.5px', color: C.muted }) }, text);
const mono = (text, style = {}) => h('span', { style: css({ fontFamily: MONO, ...style }) }, text);

/** Small action buttons drawn the way the design draws them. */
export const pill = (label, onClick, { primary = false, title: tip = null, muted = false, key } = {}) => h('div', {
  key,
  ...tap(onClick, tip),
  class: 'pt-button',
  title: tip,
  style: css({
    height: '34px', display: 'flex', alignItems: 'center', gap: '6px', padding: '0 14px',
    borderRadius: '8px', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
    fontFamily: MONO, fontSize: '11px', fontWeight: 700, letterSpacing: '.6px',
    background: primary ? C.accent : muted ? C.stroke : 'transparent',
    color: primary ? C.bg : muted ? C.muted : C.textDim,
    border: primary || muted ? 'none' : `1px solid ${C.stroke}`
  })
}, label);

/** A row of options of which one is chosen. */
export const segmented = (options, chosen, onPick, { grow = false, key } = {}) => h('div', {
  key,
  role: 'radiogroup',
  style: css({
    display: 'flex', flexWrap: 'wrap', padding: '3px', gap: '3px', borderRadius: '9px',
    background: C.panel, border: `1px solid ${C.stroke}`, ...(grow ? { flex: '1 1 100%' } : {})
  })
}, ...options.map(option => h('div', {
  key: String(option.value),
  ...tap(() => onPick(option.value), option.label),
  role: 'radio',
  'aria-checked': option.value === chosen ? 'true' : 'false',
  style: css({
    ...(grow ? { flex: '1 1 0', minWidth: '96px' } : {}),
    minHeight: option.sub ? '44px' : '30px', display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: '2px', padding: option.sub ? '6px 12px' : '0 12px', borderRadius: '7px', cursor: 'pointer',
    background: option.value === chosen ? (option.tone === 'in' ? GAIN_BG : C.strokeHi) : 'transparent',
    color: option.value === chosen ? (option.tone === 'in' ? C.accentHi : C.text) : C.muted,
    fontSize: '12.5px', fontWeight: 600
  })
},
  h('span', { style: css({ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }) },
    option.label, option.count !== undefined && mono(String(option.count), { fontSize: '10.5px', color: C.faint })),
  option.sub && h('span', {
    key: 'sub',
    style: css({ fontFamily: MONO, fontSize: '10px', lineHeight: 1.35, textAlign: 'center', fontWeight: 400, color: option.value === chosen ? C.accent : C.faint })
  }, option.sub)
)));

/** Category chips: the fixed ones, the person's own, each with its colour. */
const chips = (t, keys, chosen, onPick, key) => h('div', {
  key, style: css({ display: 'flex', flexWrap: 'wrap', gap: '6px' })
}, ...keys.map(category => h('div', {
  key: category,
  ...tap(() => onPick(category), expenseLabel(t, category)),
  'aria-pressed': chosen === category ? 'true' : 'false',
  style: css({
    height: '32px', display: 'flex', alignItems: 'center', gap: '7px', padding: '0 11px', borderRadius: '16px',
    border: `1px solid ${chosen === category ? categoryColor(category) : C.stroke}`,
    background: chosen === category ? C.panel : 'transparent', cursor: 'pointer',
    fontSize: '12.5px', color: chosen === category ? C.text : C.textDim
  })
},
  h('span', { style: css({ width: '8px', height: '8px', borderRadius: '2px', background: categoryColor(category) }) }),
  expenseLabel(t, category)
)));

const fieldStyle = { height: '42px', borderRadius: '10px', fontSize: '13.5px', padding: '0 12px', border: `1px solid ${C.strokeHi}` };

// ------------------------------------------------------------- figures ---

export function cashKpis({ metrics, t, fmt, state, actions, compact = false }) {
  const s = t.shell;
  const k = metrics.totals;
  const left = k.income - k.expenses;
  const summary = actions.fixedCostSummary();
  const ofIncome = value => (k.income > 0 ? Math.round((value / k.income) * 100) + '%' : null);

  /*
   * Income and spending double as filters: a click shows only that half of the
   * transactions, and the tile stays lit while the list is filtered to it --
   * lit by the list's own tab, so the two can never disagree.
   */
  const tab = state?.cashUi?.tab || 'all';
  const filter = which => ({
    ...tap(() => actions.setCashUi({ tab: tab === which ? 'all' : which, cat: null }),
      which === 'in' ? s.cfShowIncome : s.cfShowSpend),
    class: 'pt-button',
    title: tab === which ? s.cfShowAll : (which === 'in' ? s.cfShowIncome : s.cfShowSpend),
    'aria-pressed': tab === which ? 'true' : 'false'
  });

  const tile = (label, value, sub, { color = C.text, highlight = false, unit = null, picks = null, ring = C.accent } = {}) => h('div', {
    ...(picks ? filter(picks) : {}),
    style: css({
      padding: compact ? '12px 13px' : '16px 18px', borderRadius: '14px', minWidth: 0,
      border: `1px solid ${picks && tab === picks ? ring : highlight ? GAIN_LINE : C.stroke}`,
      background: picks && tab === picks ? C.panel : highlight ? GAIN_BG : C.card,
      boxShadow: picks && tab === picks ? `0 0 0 1px ${ring}` : 'none',
      cursor: picks ? 'pointer' : 'default',
      transition: 'border-color .15s ease, box-shadow .15s ease, background .15s ease',
      display: 'flex', flexDirection: 'column', gap: compact ? '5px' : '7px'
    })
  },
    h('div', {
      style: css({ fontFamily: MONO, fontSize: compact ? '10px' : '10.5px', letterSpacing: '1.2px', color: highlight ? GAIN_LABEL : C.muted })
    }, label),
    h('div', {
      style: css({ fontFamily: MONO, fontSize: compact ? '18px' : '26px', fontWeight: 700, letterSpacing: compact ? 0 : '-0.6px', color, whiteSpace: 'nowrap' })
    }, value, unit && h('span', { key: 'u', style: css({ fontSize: compact ? '11px' : '14px', fontWeight: 400, color: C.muted, marginLeft: '3px' }) }, unit)),
    h('div', { style: css({ fontSize: compact ? '11.5px' : '12.5px', color: highlight ? '#B4CCC4' : C.textDim, lineHeight: 1.4 }) }, sub)
  );

  return h('div', {
    style: css({
      display: 'grid', gap: compact ? '10px' : '14px',
      gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(auto-fit, minmax(210px, 1fr))'
    })
  },
    tile(s.cfIncome, fmt.money(k.income), s.cfSources(metrics.incomeRows.length), { color: C.accent, picks: 'in' }),
    tile(s.cfSpend, fmt.money(k.expenses), s.cfItems(metrics.expenseRows.length, metrics.catBars.length),
      { picks: 'out', ring: C.textDim }),
    tile(s.cfLeft, fmt.money(left), ofIncome(left) ? s.cfOfIncome(ofIncome(left)) : '—',
      { color: left >= 0 ? C.accent : DANGER_TEXT, highlight: true }),
    tile(s.cfFixed, fmt.money(summary.expense), s.cfFixedSub(ofIncome(summary.expense), summary.count),
      { unit: s.cfPerMonth('').trim() })
  );
}

// ---------------------------------------------------------------- flow ---

/**
 * Where the income went: each category as a share of the month's income
 * (or of its spending, when that is larger), and what is left over. A click
 * on a category filters the transactions under it.
 */
export function flowCard({ metrics, t, fmt, state, actions, compact = false }) {
  const s = t.shell;
  const k = metrics.totals;
  const ui = state.cashUi;
  const left = k.income - k.expenses;
  const base = Math.max(k.income, k.expenses) || 1;
  if (!metrics.catBars.length && !(left > 0)) return null;

  const segments = [
    ...metrics.catBars.map(row => ({ key: row.key, name: expenseLabel(t, row.key), value: row.value, color: categoryColor(row.key) })),
    ...(left > 0 ? [{ key: '__left', name: s.cfFlowLeft, value: left, color: C.accent }] : [])
  ];
  const pick = key => {
    if (key === '__left') return;
    actions.setCashUi({ cat: ui.cat === key ? null : key, tab: 'out' });
  };
  const share = value => Math.round((value / base) * 100);

  return box(compact, { padding: compact ? '14px' : '18px 20px', gap: '14px' },
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }) },
      title(compact, s.cfFlowTitle), !compact && hint(s.cfFlowHint)),
    h('div', {
      style: css({ display: 'flex', height: compact ? '26px' : '34px', borderRadius: '9px', overflow: 'hidden', gap: '2px', background: C.panel })
    }, ...segments.map(seg => h('div', {
      key: seg.key,
      ...(seg.key === '__left' ? {} : tap(() => pick(seg.key), seg.name)),
      title: seg.name + ': ' + fmt.money(seg.value),
      style: css({
        width: pct(seg.value / base), minWidth: '4px', height: '100%', display: 'flex', alignItems: 'center',
        padding: '0 10px', overflow: 'hidden', cursor: seg.key === '__left' ? 'default' : 'pointer',
        opacity: !ui.cat || ui.cat === seg.key ? 1 : 0.35,
        background: seg.key === '__left'
          ? `repeating-linear-gradient(135deg, ${C.accent} 0 6px, ${C.accentHi} 6px 12px)`
          : seg.color
      })
    }, !compact && share(seg.value) >= 22 && h('span', {
      key: 'in',
      style: css({ fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, whiteSpace: 'nowrap', color: inkOn(seg.key === '__left' ? C.accent : seg.color) })
    }, seg.name + ' · ' + share(seg.value) + '%')))),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '8px' }) },
      ...segments.map(seg => h('div', {
        key: 'c' + seg.key,
        ...(seg.key === '__left' ? {} : tap(() => pick(seg.key), seg.name)),
        style: css({
          display: 'flex', alignItems: 'center', gap: compact ? '6px' : '8px', height: compact ? '30px' : '36px',
          padding: compact ? '0 10px' : '0 12px',
          borderRadius: '18px', cursor: seg.key === '__left' ? 'default' : 'pointer',
          border: `1px solid ${ui.cat === seg.key ? C.ghost : C.stroke}`,
          background: ui.cat === seg.key ? C.panel : 'transparent',
          opacity: !ui.cat || ui.cat === seg.key ? 1 : 0.45
        })
      },
        h('span', { style: css({ width: '9px', height: '9px', borderRadius: '2px', background: seg.color }) }),
        h('span', { style: css({ fontSize: compact ? '11.5px' : '12.5px', color: C.textDim, whiteSpace: 'nowrap' }) }, seg.name),
        mono(fmt.money(seg.value), { fontSize: compact ? '11px' : '12px', color: C.text, whiteSpace: 'nowrap' }),
        /* The phone keeps to name and amount: the bar above already shows the share. */
        !compact && mono(share(seg.value) + '%', { key: 'pc', fontSize: '11px', color: C.muted })
      )))
  );
}

// -------------------------------------------------------- transactions ---

/** Import and export, as the two buttons in the transactions' header. */
function statementButtons({ state, t, actions }) {
  const s = t.shell;
  const limit = planLimit(state, 'csvImports');
  const used = state.user?.plan?.used?.csvImports || 0;
  const spent = limit !== null && used >= limit;
  const save = () => {
    const result = actions.exportCsv();
    if (!result || !result.rows) return actions.showError('export:empty');
    saveFile('portfolio-' + localDay() + '.csv', '﻿' + result.text, 'text/csv');
  };
  const look = {
    height: '34px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '8px',
    border: `1px solid ${C.stroke}`, fontFamily: MONO, fontSize: '11px', fontWeight: 700, letterSpacing: '.6px',
    color: C.textDim, cursor: 'pointer', whiteSpace: 'nowrap'
  };
  return {
    buttons: [
      /* With the free imports used, the button says why instead of opening a file dialog. */
      spent
        ? h('div', { key: 'imp', ...tap(() => actions.showError('api:plan_imports'), s.dataImport), class: 'pt-button', style: css({ ...look, opacity: 0.45 }) }, s.dataImport)
        : h('label', {
          key: 'imp', class: 'pt-button', role: 'button', tabindex: '0', title: s.dataNote(s.monthFull(state.month)),
          onkeydown: event => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.currentTarget.querySelector('input[type=file]')?.click();
          },
          style: css(look)
        },
          s.dataImport,
          h('input', {
            type: 'file', accept: '.csv,text/csv,text/plain', style: css({ display: 'none' }),
            onchange: event => {
              const file = event.target.files?.[0];
              event.target.value = '';
              actions.openImport(file);
            }
          })),
      h('div', { key: 'exp', ...tap(save, s.dataExport), class: 'pt-button', style: css({ ...look, opacity: state.data ? 1 : 0.45 }) }, s.dataExport)
    ],
    line: limit !== null ? planLine(spent ? s.planImportsSpent : s.planImportsUsed(used, limit), { spent, key: 'plan' }) : null
  };
}

export function transactionsCard({ metrics, t, fmt, state, inputs, actions, compact = false }) {
  const s = t.shell;
  const ui = state.cashUi;
  const k = metrics.totals;
  const base = state.data?.settings?.baseCurrency || state.settings?.baseCurrency;
  const rates = metrics.rates || {};
  const templates = state.data?.recurring || [];
  const fixedNames = new Set(templates.filter(row => row.kind === 'expense').map(row => row.label));
  const fixedIncome = new Set(templates.filter(row => row.kind === 'income').map(row => row.label));

  /** The row in the base currency, and how it was converted when it was. */
  const inBase = row => (row.currency === base ? row.amount : (rates[row.currency] ? row.amount * rates[row.currency] : null));
  const original = row => (row.currency === base ? null
    : rates[row.currency]
      ? s.cfRate(fmt.amount(row.amount, row.currency), fmt.number(rates[row.currency], 2))
      : fmt.amount(row.amount, row.currency) + ' · ' + s.cfNoRate);

  const incomes = metrics.incomeRows.map(row => ({
    id: row.id, kind: 'income', name: incomeLabel(t, row), value: inBase(row), orig: original(row),
    fixed: fixedIncome.has(row.label)
  }));
  const expenses = metrics.expenseRows.map(row => {
    const text = expenseText(t, row);
    return {
      id: row.id, kind: 'expense', cat: row.label, name: text.primary, value: inBase(row), orig: original(row),
      /* Posted from a fixed cost, or carrying one's name -- the way the app itself tells. */
      fixed: row.origin === 'recurring' || (!row.origin && row.note && fixedNames.has(row.note))
    };
  });

  const byCat = new Map();
  for (const row of expenses) {
    if (!byCat.has(row.cat)) byCat.set(row.cat, []);
    byCat.get(row.cat).push(row);
  }
  const catOrder = [...byCat.keys()].sort((a, b) => sum(byCat.get(b)) - sum(byCat.get(a)));

  const groups = [];
  if (ui.tab !== 'out' && !ui.cat && incomes.length) groups.push({ key: '__in', name: s.cfIncomes, color: C.accent, rows: incomes, income: true });
  if (ui.tab !== 'in') {
    for (const cat of catOrder) {
      if (ui.cat && ui.cat !== cat) continue;
      groups.push({ key: cat, name: expenseLabel(t, cat), color: categoryColor(cat), rows: byCat.get(cat), income: false });
    }
  }

  const group = g => {
    const total = sum(g.rows);
    const max = Math.max(1, ...g.rows.map(row => row.value || 0));
    const open = !ui.closed[g.key];
    return h('div', { key: 'g' + g.key, style: css({ display: 'flex', flexDirection: 'column' }) },
      h('div', {
        ...tap(() => actions.setCashUi({ closed: { ...ui.closed, [g.key]: open } }), g.name),
        'aria-expanded': open ? 'true' : 'false',
        style: css({
          display: 'grid', gridTemplateColumns: compact ? '12px minmax(0,1fr) auto' : '14px minmax(0,1fr) auto 120px',
          gap: '12px', alignItems: 'center', padding: compact ? '12px 14px 8px' : '12px 18px 8px', cursor: 'pointer'
        })
      },
        h('span', { style: css({ width: '10px', height: '10px', borderRadius: '3px', background: g.color }) }),
        h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 }) },
          h('span', { style: css({ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, g.name),
          mono(s.cfCount(g.rows.length), { fontSize: '11px', color: C.muted, whiteSpace: 'nowrap' })),
        !compact && mono(g.income || !(k.income > 0) ? '' : s.cfShare(Math.round((total / k.income) * 100)), { fontSize: '11px', color: C.muted }),
        h('div', { style: css({ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }) },
          mono((g.income ? '+' : '') + fmt.money(total), { fontSize: '13.5px', fontWeight: 700, color: g.income ? C.accent : C.text, whiteSpace: 'nowrap' }),
          h('span', { style: css({ fontSize: '11px', color: C.muted, width: '10px', textAlign: 'center' }) }, open ? '▾' : '▸'))
      ),
      open && h('div', { key: 'rows' }, ...g.rows.slice().sort((a, b) => (b.value || 0) - (a.value || 0)).map(row => h('div', {
        key: row.id,
        style: css({
          display: 'grid',
          gridTemplateColumns: compact ? '12px minmax(0,1fr) auto 30px' : '14px minmax(120px,1fr) minmax(0,90px) 120px 32px',
          gap: compact ? '10px' : '12px', alignItems: 'center', minHeight: '44px', padding: compact ? '0 14px' : '0 18px'
        })
      },
        h('span'),
        h('div', {
          ...tap(() => actions.askEdit(row.kind, row.id), row.kind === 'income' ? s.editIncomeTitle : s.editExpenseTitle),
          title: row.kind === 'income' ? s.editIncomeTitle : s.editExpenseTitle,
          style: css({
            display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, height: '100%', cursor: 'pointer',
            borderBottom: `1px solid ${C.line}`, flexWrap: compact ? 'wrap' : 'nowrap', padding: compact ? '8px 0' : 0
          })
        },
          h('span', { style: css({ fontSize: '13.5px', color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }) }, row.name),
          row.fixed && h('span', {
            key: 'fx',
            style: css({
              flex: '0 0 auto', padding: '2px 6px', borderRadius: '5px', border: `1px solid ${C.ghost}`,
              fontFamily: MONO, fontSize: '10px', letterSpacing: '.6px', color: C.textDim
            })
          }, s.cfFixedBadge),
          row.orig && mono(row.orig, { key: 'o', flex: '0 0 auto', fontSize: '11px', color: C.muted, whiteSpace: 'nowrap' })
        ),
        !compact && h('div', {
          key: 'bar',
          style: css({ height: '100%', display: 'flex', alignItems: 'center', borderBottom: `1px solid ${C.line}` })
        }, h('div', { style: css({ width: '100%', height: '4px', borderRadius: '2px', background: C.stroke, overflow: 'hidden' }) },
          h('div', { style: css({ height: '100%', width: pct((row.value || 0) / max), background: g.color }) }))),
        h('div', {
          style: css({
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', borderBottom: `1px solid ${C.line}`,
            fontFamily: MONO, fontSize: '13px', whiteSpace: 'nowrap', color: g.income ? C.accent : C.text
          })
        }, row.value === null ? '—' : (g.income ? '+' : '') + fmt.money(row.value, 2)),
        h('div', {
          ...tap(() => (row.kind === 'income' ? actions.removeIncome(row.id) : actions.removeExpense(row.id)), s.removeLabel || 'Usuń'),
          class: 'pt-button',
          title: s.removeLabel,
          style: css({
            height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px',
            color: C.faint, cursor: 'pointer', fontSize: '15px'
          })
        }, '×')
      )))
    );
  };

  const statements = statementButtons({ state, t, actions });
  const addOpen = ui.adding && !compact;

  return box(compact, {},
    h('div', {
      style: css({
        padding: compact ? '14px 14px 12px' : '16px 18px 14px', display: 'flex', flexDirection: 'column', gap: '12px',
        borderBottom: `1px solid ${C.line}`
      })
    },
      h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }) },
        title(compact, s.cfTx),
        hint(s.monthFull(state.month)),
        h('div', { style: css({ flex: 1 }) }),
        ...statements.buttons,
        compact
          ? pill(s.cfAdd, () => actions.setSheet('expense'), { primary: true, key: 'add' })
          : pill(addOpen ? s.cfClose : s.cfAdd, () => actions.setCashUi({ adding: !ui.adding }), { primary: !addOpen, muted: addOpen, key: 'add' })
      ),
      statements.line,
      h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }) },
        segmented([
          { value: 'all', label: s.cfAll, count: incomes.length + expenses.length },
          { value: 'in', label: s.cfIncomes, count: incomes.length },
          { value: 'out', label: s.cfExpenses, count: expenses.length }
        ], ui.tab, value => actions.setCashUi({ tab: value, cat: value === 'in' ? null : ui.cat }), { key: 'tabs' }),
        ui.cat && h('div', {
          key: 'cat',
          ...tap(() => actions.setCashUi({ cat: null }), s.cfFilterClear),
          title: s.cfFilterClear,
          style: css({
            height: '30px', display: 'flex', alignItems: 'center', gap: '7px', padding: '0 10px', borderRadius: '15px',
            background: C.panel, border: `1px solid ${C.ghost}`, fontSize: '12px', color: C.text, cursor: 'pointer'
          })
        },
          h('span', { style: css({ width: '8px', height: '8px', borderRadius: '2px', background: categoryColor(ui.cat) }) }),
          expenseLabel(t, ui.cat), h('span', { style: css({ color: C.muted }) }, '×'))
      )
    ),
    addOpen && addForm({ t, state, inputs, actions }),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', padding: '4px 0 8px' }) },
      groups.length === 0
        ? h('div', { key: 'empty', style: css({ padding: compact ? '14px' : '16px 18px' }) }, emptyLine(s.cfEmptyTx))
        : groups.map(group))
  );
}

const sum = rows => rows.reduce((total, row) => total + (row.value || 0), 0);

/** The inline form: an expense or an income, and optionally a fixed cost from now on. */
function addForm({ t, state, inputs, actions }) {
  const s = t.shell;
  const ui = state.cashUi;
  const out = ui.addKind === 'expense';
  const save = () => {
    const amount = parseNumber(inputs.value('cfAmt'));
    if (amount === null || amount <= 0) return actions.showError(s.amountNeeded);
    const name = inputs.value('cfName').trim();
    const own = inputs.value('cfCustom').trim();
    const category = out ? (ui.addCat === 'Other' && own ? expenseCategory(own) || 'Other' : ui.addCat) : null;
    if (out && ui.addCat === 'Other' && own && !expenseCategory(own)) return actions.showError(s.labelNeeded);
    const label = name || (out ? expenseLabel(t, category) : t.phSource);
    const dayText = inputs.value('cfDay').trim();
    const day = dayText ? parseNumber(dayText) : null;
    if (ui.recurring && dayText && (day === null || day < 1 || day > 31)) return actions.showError(s.amountNeeded);

    if (out) actions.addExpense({ label: category, amount, note: label });
    else actions.addIncome({ label, amount });
    /*
     * Marked as already posted this month: the entry just added is this
     * month's, and the fixed cost finds it by its name (recurringEntryIn), so
     * the "add to transactions" button does not offer it a second time.
     */
    if (ui.recurring) {
      actions.addRecurring({
        kind: ui.addKind, label, amount, everyMonths: 1,
        ...(out ? { category } : {}),
        dayOfMonth: day ? Math.round(day) : null,
        lastPostedMonth: state.month
      });
    }
    inputs.clear(['cfAmt', 'cfName', 'cfCustom', 'cfDay']);
    actions.setCashUi({ adding: false, recurring: false });
  };

  return h('div', {
    key: 'add-form',
    class: 'pt-rise',
    style: css({
      padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px',
      background: C.panel, borderBottom: `1px solid ${C.line}`
    })
  },
    h('div', { style: css({ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }) },
      segmented([{ value: 'expense', label: s.cfOut }, { value: 'income', label: s.cfIn, tone: 'in' }],
        ui.addKind, value => actions.setCashUi({ addKind: value }), { key: 'kind' }),
      h('div', { style: css({ flex: '1 1 160px', minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }) },
        input(inputs, 'cfAmt', numberText('0.00'), {
          type: 'number', onEnter: save, ariaLabel: s.vhAmount,
          style: { ...fieldStyle, flex: 1, fontFamily: MONO, fontSize: '17px', fontWeight: 700 }
        }),
        currencyPicker({ state, actions, style: { height: '42px', borderRadius: '10px' } })),
      input(inputs, 'cfName', out ? s.cfNameOut : s.cfNameIn, { onEnter: save, style: { ...fieldStyle, flex: '2 1 200px' } })
    ),
    out && chips(t, categoryChoices(state), ui.addCat, value => actions.setCashUi({ addCat: value }), 'cats'),
    out && ui.addCat === 'Other' && input(inputs, 'cfCustom', s.cfCustom, { onEnter: save, style: { ...fieldStyle, borderColor: CASH } }),
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }) },
      h('div', {
        ...tap(() => actions.setCashUi({ recurring: !ui.recurring }), s.cfRecurringToggle),
        role: 'switch', 'aria-checked': ui.recurring ? 'true' : 'false',
        style: css({ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer', minHeight: '36px' })
      },
        h('div', { style: css({ width: '36px', height: '20px', borderRadius: '10px', background: ui.recurring ? C.accent : C.strokeHi, position: 'relative', transition: 'background .15s' }) },
          h('div', { style: css({ position: 'absolute', top: '2px', left: ui.recurring ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: C.text, transition: 'left .15s' }) })),
        h('span', { style: css({ fontSize: '13px', color: C.textDim }) }, s.cfRecurringToggle)
      ),
      ui.recurring && h('div', { key: 'day', style: css({ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: C.muted }) },
        s.cfDay, input(inputs, 'cfDay', '1', { type: 'number', onEnter: save, ariaLabel: s.cfDay, style: { width: '60px', height: '34px', borderRadius: '8px', fontFamily: MONO, fontSize: '13px' } })),
      h('div', { style: css({ flex: 1 }) }),
      h('div', {
        ...tap(() => actions.setCashUi({ adding: false }), s.cfCancel),
        style: css({ height: '38px', display: 'flex', alignItems: 'center', padding: '0 14px', borderRadius: '9px', color: C.muted, fontSize: '13px', cursor: 'pointer' })
      }, s.cfCancel),
      pill(s.cfSave, save, { primary: true, key: 'save' })
    )
  );
}

// ---------------------------------------------------------- fixed costs ---

export function fixedCard({ metrics, t, fmt, state, inputs, actions, compact = false }) {
  const s = t.shell;
  const ui = state.cashUi;
  const templates = state.data?.recurring || [];
  const summary = actions.fixedCostSummary();
  const pending = actions.pendingRecurring();
  const inMonth = actions.recurringInMonth();
  const again = pending.length === 0 && inMonth.length > 0;
  const base = state.data?.settings?.baseCurrency || state.settings?.baseCurrency;
  const rates = metrics.rates || {};
  const income = metrics.totals.income;
  const toBase = row => (row.currency === base ? row.amount : (rates[row.currency] ? row.amount * rates[row.currency] : null));
  const every = row => (CADENCES.includes(row.everyMonths) ? row.everyMonths : 1);
  const short = key => t.months[parseInt(String(key).slice(5, 7), 10) - 1];

  const expenses = templates.filter(row => row.kind === 'expense');
  const incomes = templates.filter(row => row.kind === 'income');
  const monthly = expenses.filter(row => every(row) === 1).sort((a, b) => (a.dayOfMonth || 99) - (b.dayOfMonth || 99));
  const rarer = expenses.filter(row => every(row) !== 1);

  // ---- the month as a strip of days ----
  const [y, m] = state.month.split('-').map(Number);
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const today = new Date();
  const isNow = state.month === currentMonth();
  const charged = new Map();
  let chargedTotal = 0;
  for (const row of expenses) {
    if (!row.active || !recurringFallsIn(row, state.month)) continue;
    const value = toBase(row) || 0;
    chargedTotal += value;
    if (!row.dayOfMonth) continue;
    const day = Math.min(row.dayOfMonth, days);
    const entry = charged.get(day) || { sum: 0, names: [], color: categoryColor(row.category || 'Other') };
    entry.sum += value;
    entry.names.push(row.label);
    charged.set(day, entry);
  }
  const top = Math.max(1, ...[...charged.values()].map(entry => entry.sum));
  const calendar = h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
    h('div', { style: css({ display: 'flex', justifyContent: 'space-between', fontFamily: MONO, fontSize: '10.5px', letterSpacing: '1.1px', color: C.muted }) },
      h('span', null, s.monthFull(state.month).toUpperCase()), h('span', null, s.cfCharged(fmt.money(chargedTotal)))),
    h('div', {
      style: css({ display: 'grid', gridTemplateColumns: `repeat(${compact ? 16 : days}, minmax(0, 1fr))`, gap: '2px' })
    }, ...Array.from({ length: days }, (_, i) => {
      const day = i + 1;
      const weekday = new Date(Date.UTC(y, m - 1, day)).getUTCDay();
      const weekend = weekday === 0 || weekday === 6;
      const isToday = isNow && day === today.getDate();
      const entry = charged.get(day);
      const size = entry ? Math.round(6 + (entry.sum / top) * 8) : 0;
      return h('div', {
        key: 'd' + day,
        title: entry ? day + ' ' + short(state.month) + ': ' + entry.names.join(', ') + ' — ' + fmt.money(entry.sum) : day + ' ' + short(state.month),
        style: css({
          height: compact ? '40px' : '46px', borderRadius: '5px', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'space-between', padding: '4px 0 5px',
          background: isToday ? 'rgba(227,194,122,.10)' : weekend ? 'rgba(130,150,168,.04)' : 'rgba(130,150,168,.09)',
          border: `1px solid ${isToday ? C.goldDeep : 'transparent'}`
        })
      },
        h('span', { style: css({ fontFamily: MONO, fontSize: '9.5px', color: isToday ? C.gold : weekend ? C.faint : C.muted }) }, String(day)),
        h('span', { style: css({ width: size + 'px', height: size + 'px', borderRadius: '50%', background: entry ? entry.color : 'transparent' }) })
      );
    }))
  );

  // ---- lists ----
  const amountOf = row => {
    const value = toBase(row);
    return row.currency === base
      ? { main: fmt.amount(row.amount, row.currency), sub: null }
      : { main: fmt.amount(row.amount, row.currency), sub: value === null ? s.cfNoRate : '≈ ' + fmt.money(value) };
  };
  /*
   * Editing opens the form and brings it into view: it sits under the lists,
   * which on a phone is a screen or two below the ✎ that was pressed.
   */
  const startEdit = row => {
    actions.setCashUi({
      fxOpen: true, fxId: row.id, fxKind: row.kind, fxEvery: every(row), fxCat: row.category || 'Other', fxRev: ui.fxRev + 1
    });
    if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined') {
      requestAnimationFrame(() => document.getElementById('fx-form')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };
  const line = (row, { dayText, sub }) => h('div', {
    key: row.id,
    style: css({
      display: 'grid', gridTemplateColumns: compact ? '34px 8px minmax(0,1fr) auto auto' : '44px 10px minmax(0,1fr) auto auto',
      gap: compact ? '8px' : '10px', alignItems: 'center', padding: compact ? '6px 0' : 0,
      minHeight: '44px', borderBottom: `1px solid ${C.line}`, opacity: row.active ? 1 : 0.55,
      background: ui.fxId === row.id ? C.panel : 'transparent'
    })
  },
    mono(dayText, { fontSize: '12px', color: C.textDim, whiteSpace: 'nowrap' }),
    h('span', { style: css({ width: '8px', height: '8px', borderRadius: '2px', background: row.kind === 'income' ? C.accent : categoryColor(row.category || 'Other') }) }),
    /* On a phone the category goes under the name, or the name is cut to its first letter. */
    h('div', {
      style: css(compact
        ? { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }
        : { display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 })
    },
      h('span', { style: css({ fontSize: '13.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, row.label),
      h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) },
        !row.active ? s.cfPaused : row.kind === 'income' ? '' : expenseLabel(t, row.category || 'Other'))),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }) },
      mono((row.kind === 'income' ? '+' : '') + amountOf(row).main, { fontSize: '13px', color: row.kind === 'income' ? C.accent : C.text, whiteSpace: 'nowrap' }),
      (sub || amountOf(row).sub) && h('span', { key: 'sub', style: css({ fontSize: '11px', color: C.muted, whiteSpace: 'nowrap' }) }, sub || amountOf(row).sub)),
    h('div', { style: css({ display: 'flex', gap: '4px' }) },
      h('div', {
        ...tap(() => startEdit(row), s.editFixedTitle), class: 'pt-button', title: s.editFixedTitle,
        style: css({ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: `1px solid ${C.stroke}`, color: C.muted, fontSize: '12px', cursor: 'pointer' })
      }, '✎'),
      h('div', {
        ...tap(() => actions.removeRecurring(row.id), s.removeLabel), class: 'pt-button', title: s.removeLabel,
        style: css({ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', color: C.faint, fontSize: '15px', cursor: 'pointer' })
      }, '×'))
  );
  const dayOf = row => (row.dayOfMonth ? row.dayOfMonth + '.' : '—');
  const nextOf = row => {
    const next = nextChargeMonth(row, currentMonth());
    return (row.dayOfMonth ? row.dayOfMonth + ' ' : '') + (next ? short(next) : '—');
  };
  const head = (text, count, total, key) => h('div', {
    key,
    style: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0 8px', borderBottom: `1px solid ${C.ghost}` })
  },
    mono(text + ' · ' + count, { fontSize: '11px', letterSpacing: '1.2px', color: C.textDim }),
    mono(total, { fontSize: '12.5px', fontWeight: 700 }));
  const totalOf = rows => rows.reduce((x, row) => x + (toBase(row) || 0), 0);

  const lists = [
    incomes.length > 0 && h('div', { key: 'inc' },
      head(s.cfIncomeHead, incomes.length, '+' + fmt.money(totalOf(incomes.filter(row => every(row) === 1))), 'h'),
      ...incomes.map(row => line(row, { dayText: every(row) === 1 ? dayOf(row) : nextOf(row) }))),
    monthly.length > 0 && h('div', { key: 'mon' },
      head(s.cfEveryHead[1], monthly.length, fmt.money(totalOf(monthly)), 'h'),
      ...monthly.map(row => line(row, { dayText: dayOf(row) }))),
    ...[3, 6, 12].map(n => {
      const rows = rarer.filter(row => every(row) === n);
      if (!rows.length) return null;
      return h('div', { key: 'r' + n },
        head(s.cfEveryHead[n], rows.length, fmt.money(totalOf(rows)) + '/' + s.cfPer[n], 'h'),
        ...rows.map(row => line(row, {
          dayText: nextOf(row),
          sub: toBase(row) === null ? null : s.cfApproxMonth(fmt.money(toBase(row) / n, 2))
        })));
    })
  ].filter(Boolean);

  /*
   * Two buttons on this card add things, and they were easy to mix up: this
   * one writes the month's fixed costs into the transactions list; the one
   * under the lists creates a new fixed cost. Each says which in a line of its
   * own, naming the month it acts on.
   */
  const post = pending.length > 0
    ? pill(s.cfPostN(pending.length), () => actions.postRecurring(), { primary: true, key: 'post', title: pending.map(row => row.label).join(', ') })
    : again
      ? pill(s.cfAllPosted, actions.askRecurringAgain, { muted: true, key: 'post', title: s.cfAllPostedTitle })
      : null;
  const postHint = pending.length > 0
    ? s.cfPostHint(pending.length, state.month)
    : again ? s.cfAllPostedHint(state.month) : null;
  const button = post && h('div', {
    key: 'post-box',
    /* A row of its own under the heading: the button, and beside it what it will do. */
    style: css({
      flex: '1 1 100%', display: 'flex', flexDirection: compact ? 'column' : 'row',
      alignItems: compact ? 'stretch' : 'center', gap: compact ? '5px' : '12px'
    })
  }, post, h('div', {
    style: css({ fontSize: compact ? '11px' : '12px', lineHeight: 1.4, color: C.muted })
  }, postHint));

  const share = income > 0 ? Math.round((summary.expense / income) * 100) + '%' : null;

  return box(compact, { padding: compact ? '14px' : '16px 18px', gap: '16px', height: compact ? 'auto' : '100%', boxSizing: 'border-box' },
    h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }) },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 200px', minWidth: 0 }) },
        title(compact, s.fixedCosts),
        hint(s.cfFxSub(share, monthly.length, rarer.length))),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }) },
        mono(fmt.money(summary.expense), { fontSize: '20px', fontWeight: 700 }),
        h('div', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.cfMonthly)),
      button
    ),
    calendar,
    ...(lists.length ? lists : [h('div', { key: 'empty', style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.5 }) }, s.fixedEmpty)]),
    summary.unconverted.length > 0 && h('div', { key: 'unc', style: css({ fontSize: '11px', color: C.muted }) }, s.fixedUnconverted(summary.unconverted.length)),
    /* Folded until asked for, as the transactions' form is. */
    (ui.fxOpen || ui.fxId)
      ? fixedForm({ t, state, inputs, actions, compact })
      : h('div', {
        key: 'fx-open',
        ...tap(() => actions.setCashUi({ fxOpen: true, fxId: null, fxRev: ui.fxRev + 1 }), s.cfOpenFixed),
        class: 'pt-button',
        style: css({
          minHeight: compact ? '52px' : '48px', padding: '8px 12px', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '3px', textAlign: 'center',
          borderRadius: '10px', border: `1px dashed ${C.strokeHi}`, cursor: 'pointer'
        })
      },
        h('span', { style: css({ color: C.accent, fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, letterSpacing: '.8px' }) }, s.cfOpenFixed),
        h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.cfOpenFixedHint)),
    h('div', { style: css({ flex: 1 }) }),
    h('div', { style: css({ fontSize: '12px', lineHeight: 1.5, color: C.muted }) },
      s.cfFxNoteA,
      h('span', {
        style: css({ padding: '1px 5px', borderRadius: '4px', border: `1px solid ${C.ghost}`, fontFamily: MONO, fontSize: '10px', color: C.textDim })
      }, s.cfFixedBadge),
      s.cfFxNoteB)
  );
}

/** Adding a fixed cost, or editing the one whose ✎ was pressed. */
function fixedForm({ t, state, inputs, actions, compact }) {
  const s = t.shell;
  const ui = state.cashUi;
  const editing = ui.fxId ? (state.data?.recurring || []).find(row => row.id === ui.fxId) : null;
  const kind = editing ? editing.kind : ui.fxKind;
  const out = kind === 'expense';
  const rarer = ui.fxEvery !== 1;
  const knownCat = editing && editing.category && CATEGORIES.includes(editing.category);
  const year = Number(state.month.slice(0, 4));
  const anchorMonth = editing?.firstMonth ? Number(editing.firstMonth.slice(5, 7)) : Number(state.month.slice(5, 7));

  const save = () => {
    const label = inputs.value('fxName').trim();
    const amount = parseNumber(inputs.value('fxAmt'));
    if (!label) return actions.showError(s.labelNeeded);
    if (amount === null || amount <= 0) return actions.showError(s.amountNeeded);
    const dayText = inputs.value('fxDay').trim();
    const day = dayText ? parseNumber(dayText) : null;
    if (dayText && (day === null || day < 1 || day > 31)) return actions.showError(s.amountNeeded);
    const own = inputs.value('fxCustom').trim();
    if (out && ui.fxCat === 'Other' && own && !expenseCategory(own)) return actions.showError(s.labelNeeded);
    const category = out ? (ui.fxCat === 'Other' && own ? expenseCategory(own) : ui.fxCat) : undefined;
    const monthNo = Number(inputs.value('fxMonth')) || anchorMonth;
    const fields = {
      label, amount,
      currency: inputs.value('fxCur') || actions.entryCurrency(),
      everyMonths: ui.fxEvery,
      dayOfMonth: day ? Math.round(day) : null,
      /* A longer cycle counts from the month it falls in; monthly needs no anchor. */
      firstMonth: rarer ? `${year}-${String(monthNo).padStart(2, '0')}` : (editing ? editing.firstMonth || null : null),
      ...(out ? { category } : {})
    };
    if (editing) actions.updateRecurring(editing.id, fields);
    else actions.addRecurring({ kind, ...fields });
    inputs.clear(['fxName', 'fxAmt', 'fxDay', 'fxCustom']);
    actions.setCashUi({ fxOpen: false, fxId: null, fxRev: ui.fxRev + 1 });
  };
  const cancel = () => actions.setCashUi({ fxOpen: false, fxId: null, fxRev: ui.fxRev + 1 });

  return h('div', {
    key: 'fxform-' + (editing ? editing.id : 'new') + '-' + ui.fxRev,
    id: 'fx-form',
    class: 'pt-rise',
    style: css({
      padding: '14px', borderRadius: '12px', background: C.panel,
      border: `1px solid ${editing ? C.goldDeep : C.stroke}`, display: 'flex', flexDirection: 'column', gap: '12px'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }) },
      mono(editing ? s.cfEditFixed : s.cfNewFixed, { fontSize: '10.5px', letterSpacing: '1.2px', color: editing ? C.gold : C.muted }),
      h('div', { style: css({ flex: 1 }) }),
      !editing && segmented([{ value: 'expense', label: s.cfOut }, { value: 'income', label: s.cfIn, tone: 'in' }],
        ui.fxKind, value => actions.setCashUi({ fxKind: value }), { key: 'kind' }),
      h('div', {
        key: 'close', ...tap(cancel, s.cfClose), class: 'pt-button', title: s.cfClose,
        style: css({
          width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '8px', color: C.muted, fontSize: '16px', cursor: 'pointer'
        })
      }, '×')
    ),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
      mono(s.cfCycle, { fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }),
      segmented(CADENCES.map(n => ({ value: n, label: s.cfCycles[n][0], sub: s.cfCycles[n][1] })),
        ui.fxEvery, value => actions.setCashUi({ fxEvery: value }), { grow: true, key: 'cycle' })),
    input(inputs, 'fxName', s.cfFxName, { value: editing ? editing.label : undefined, onEnter: save, style: { ...fieldStyle } }),
    h('div', { style: css({ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }) },
      h('div', { style: css({ flex: '1 1 160px', minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }) },
        input(inputs, 'fxAmt', numberText('0.00'), {
          type: 'number', value: editing ? numberText(editing.amount) : undefined, onEnter: save, ariaLabel: s.vhAmount,
          style: { ...fieldStyle, flex: 1, fontFamily: MONO, fontSize: '17px', fontWeight: 700 }
        }),
        select(inputs, 'fxCur', currencyOptions(state, editing ? editing.currency : actions.entryCurrency()),
          { height: '42px', borderRadius: '10px', flex: '0 0 auto' },
          { value: editing ? editing.currency : actions.entryCurrency(), ariaLabel: s.ivCurF })),
      h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: C.muted, flexWrap: 'wrap' }) },
        s.cfDay,
        input(inputs, 'fxDay', '1', {
          type: 'number', value: editing?.dayOfMonth ? String(editing.dayOfMonth) : undefined, onEnter: save, ariaLabel: s.cfDay,
          style: { width: '60px', height: '42px', borderRadius: '10px', fontFamily: MONO, fontSize: '13px' }
        }),
        rarer && h('span', { key: 'ml' }, ui.fxEvery === 12 ? s.cfInMonth : s.cfFromMonth),
        rarer && select(inputs, 'fxMonth', s.cfMonths.map((name, i) => ({ value: String(i + 1), label: name })),
          { height: '42px', borderRadius: '10px' }, { value: String(anchorMonth), ariaLabel: ui.fxEvery === 12 ? s.cfInMonth : s.cfFromMonth }))
    ),
    out && chips(t, categoryChoices(state, editing?.category), ui.fxCat, value => actions.setCashUi({ fxCat: value }), 'cats'),
    out && ui.fxCat === 'Other' && input(inputs, 'fxCustom', s.cfCustom, {
      value: editing && editing.category && !knownCat ? editing.category : undefined, onEnter: save,
      style: { ...fieldStyle, borderColor: CASH }
    }),
    h('div', { style: css({ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }) },
      editing && h('div', {
        key: 'more', ...tap(() => { cancel(); actions.askEdit('recurring', editing.id); }, s.cfMore),
        style: css({ marginRight: 'auto', fontSize: '12.5px', color: C.accent, cursor: 'pointer' })
      }, s.cfMore),
      h('div', {
        key: 'cancel', ...tap(cancel, s.cfCancel),
        style: css({ height: '38px', display: 'flex', alignItems: 'center', padding: '0 14px', borderRadius: '9px', color: C.muted, fontSize: '13px', cursor: 'pointer' })
      }, s.cfCancel),
      pill(editing ? s.cfSaveChanges : s.cfAddFixed, save, { primary: true, key: 'save' })
    )
  );
}
